/**
 * RSVP Utility Module
 *
 * Centralizes RSVP parsing, status mapping, count calculation, and update logic
 * shared across RSVP.tsx, Dashboard.tsx, EventDetail.tsx, Checkin.tsx, and Events.tsx.
 */

import { restClient } from '../api/rest-client';
import { parseError } from '../utils/errorHandling';

/** Numeric attendance status values stored in DynamoDB */
export type AttendanceValue = -1 | 0 | 1 | 2;

/** Display-friendly RSVP status */
export type RSVPDisplayStatus = 'coming' | 'maybe' | 'not-coming' | 'no-response';

/** Attendance map: rider_id → numeric status */
export type AttendanceMap = Record<string, number>;

/** RSVP count breakdown across all four categories */
export interface RSVPCounts {
  coming: number;
  maybe: number;
  notComing: number;
  noResponse: number;
}

/** Result of an RSVP update operation */
export interface RSVPUpdateResult {
  success: boolean;
  updatedAttendance: AttendanceMap;
  error?: string;
}

/**
 * Parse RSVP attendance data from either string (legacy JSON) or object (native Map) format.
 * Returns an empty object for null, undefined, or invalid input.
 */
export function parseExpectedAttendance(
  data: Record<string, number> | string | null | undefined
): AttendanceMap {
  if (data == null) return {};

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
      return {};
    } catch {
      return {};
    }
  }

  if (typeof data === 'object' && !Array.isArray(data)) {
    // Coerce string values to numbers (defensive: handles default=str serialization)
    const result: AttendanceMap = {};
    for (const [key, val] of Object.entries(data)) {
      result[key] = typeof val === 'string' ? Number(val) : (val as number);
    }
    return result;
  }

  return {};
}

/**
 * Map a numeric attendance value to a display status.
 * Returns 'no-response' for absent riders, -1, or unexpected values.
 */
export function getRSVPStatus(
  attendance: AttendanceMap,
  riderId: string
): RSVPDisplayStatus {
  if (!(riderId in attendance)) return 'no-response';

  const value = attendance[riderId];
  switch (value) {
    case 1:
      return 'coming';
    case 2:
      return 'maybe';
    case 0:
      return 'not-coming';
    case -1:
    default:
      return 'no-response';
  }
}

/**
 * Get the next status in the cycle:
 * no-response → coming → maybe → not-coming → no-response
 */
export function getNextRSVPStatus(
  current: RSVPDisplayStatus
): RSVPDisplayStatus {
  switch (current) {
    case 'no-response':
      return 'coming';
    case 'coming':
      return 'maybe';
    case 'maybe':
      return 'not-coming';
    case 'not-coming':
      return 'no-response';
    default:
      return 'coming';
  }
}

/**
 * Calculate counts for each RSVP category from an attendance map.
 * Includes the "maybe" category (value 2) that Events.tsx currently misses.
 * Sum of all counts always equals total entries in the map.
 */
export function calculateRSVPCounts(attendance: AttendanceMap): RSVPCounts {
  let coming = 0;
  let maybe = 0;
  let notComing = 0;
  let noResponse = 0;

  for (const value of Object.values(attendance)) {
    switch (value) {
      case 1:
        coming++;
        break;
      case 2:
        maybe++;
        break;
      case 0:
        notComing++;
        break;
      default:
        noResponse++;
        break;
    }
  }

  return { coming, maybe, notComing, noResponse };
}

/**
 * Convert a display status back to its numeric attendance value.
 */
export function statusToValue(status: RSVPDisplayStatus): AttendanceValue {
  switch (status) {
    case 'coming':
      return 1;
    case 'maybe':
      return 2;
    case 'not-coming':
      return 0;
    case 'no-response':
      return -1;
  }
}

/**
 * Toggle a rider's RSVP status and send the update to the API.
 * Returns the updated attendance map on success, or an error result on failure.
 * Sends only event_id + rider-level RSVP data (minimal payload).
 */
export async function updateRSVPStatus(
  event: { event_id: string; event_expectedAttendance?: AttendanceMap | string | null },
  riderId: string,
  currentStatus: RSVPDisplayStatus
): Promise<RSVPUpdateResult> {
  const originalAttendance = parseExpectedAttendance(event.event_expectedAttendance);
  const nextStatus = getNextRSVPStatus(currentStatus);
  const numericValue = statusToValue(nextStatus);

  // Build locally-mutated attendance map
  const updatedAttendance = { ...originalAttendance };
  if (nextStatus === 'no-response') {
    delete updatedAttendance[riderId];
  } else {
    updatedAttendance[riderId] = numericValue;
  }

  try {
    // Send minimal payload: only event_id + single-rider RSVP fields
    await restClient.updateEvent(event.event_id, {
      event_id: event.event_id,
      rsvp_rider_id: riderId,
      rsvp_status: numericValue,
    } as any);

    return { success: true, updatedAttendance };
  } catch (err) {
    const parsed = parseError(err);
    return { success: false, error: parsed.message, updatedAttendance: originalAttendance };
  }
}
